import { Routes, Route, Navigate } from "react-router-dom";
import Landing from "./pages/Landing";
import SignIn from "./pages/SignIn";
import Literature from "./pages/Literature";
import Home from "./pages/Home";
import CheckIn from "./pages/checkin/CheckIn";
import StepsOverview from "./pages/steps/StepsOverview";
import StepFlow from "./pages/steps/StepFlow";
import Step4 from "./pages/steps/Step4";
import Step5 from "./pages/steps/Step5";
import Step8 from "./pages/steps/Step8";
import Step9 from "./pages/steps/Step9";
import TwentyQuestions from "./pages/TwentyQuestions";
import Settings from "./pages/Settings";
import Trends from "./pages/Trends";
import EntryDetail from "./pages/EntryDetail";
import Lists from "./pages/Lists";
import ListDetail from "./pages/ListDetail";
import Sharing from "./pages/Sharing";
import BrightIdeas from "./pages/BrightIdeas";
import Onboarding from "./pages/Onboarding";
import SponsorDashboard from "./pages/SponsorDashboard";
import SponseeDetail from "./pages/SponseeDetail";
import SponseeStepView from "./pages/SponseeStepView";
import { ProtectedRoute } from "./components/protected-route";

// The public landing page is full-bleed and responsive. The app itself is
// phone-first: it lives in a centered max-480px frame on larger screens.
function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto min-h-dvh w-full max-w-[480px] bg-background">
      {children}
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/signin" element={<SignIn />} />
      {/* Public, full-bleed, and deliberately not behind ProtectedRoute: the
          point of this page is that someone can read it before deciding
          whether Happy24 fits their fellowship — or their lack of one. */}
      <Route path="/literature" element={<Literature />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <Home />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkin"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <CheckIn />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/steps"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <StepsOverview />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      {/* Step 4 breaks the reflective template — its own encrypted inventory flow. */}
      <Route
        path="/steps/4"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <Step4 />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      {/* Step 5 is the in-person acknowledgement archetype — its own short flow. */}
      <Route
        path="/steps/5"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <Step5 />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      {/* Step 8 breaks the template too — the encrypted, living amends list. */}
      <Route
        path="/steps/8"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <Step8 />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      {/* Step 9 records outcomes onto the Step 8 rows — its own flow too. */}
      <Route
        path="/steps/9"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <Step9 />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/steps/:n"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <StepFlow />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/twenty-questions"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <TwentyQuestions />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <Settings />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trends"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <Trends />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/entries/:id"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <EntryDetail />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lists"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <Lists />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lists/:id"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <ListDetail />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sharing"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <Sharing />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <Onboarding />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bright-ideas"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <BrightIdeas />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sponsor"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <SponsorDashboard />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sponsor/:ownerId"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <SponseeDetail />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sponsor/:ownerId/steps/:n"
        element={
          <ProtectedRoute>
            <PhoneFrame>
              <SponseeStepView />
            </PhoneFrame>
          </ProtectedRoute>
        }
      />
      {/* Anything unrecognised (a stale email link, a typo) goes home rather
          than rendering nothing. `/app` sends signed-out visitors to sign-in. */}
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}
